import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";

// Layout Components — always loaded
import Header from "./components/Header";
import ScrollToTopButton from "./components/ScrollToTopButton";
import CookieConsent from "./components/CookieConsent";
import IframeScrollHandler from "./components/IframeScrollHandler";
import ScrollToHash from "./components/ScrollToHash";

// HomePage eagerly loaded (most common entry point / LCP critical)
import HomePage from "./pages/HomePage";

// All other pages lazy-loaded
const AboutPage = lazy(() => import("./pages/AboutPage"));
const PortfolioPage = lazy(() => import("./pages/PortfolioPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const HostingPage = lazy(() => import("./pages/HostingPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const EmailPage = lazy(() => import("./pages/EmailPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const BlogAdminPage = lazy(() => import("./pages/BlogAdminPage"));
const SEOAdminPage = lazy(() => import("./pages/SEOAdminPage"));
const FAQAdminPage = lazy(() => import("./pages/FAQAdminPage"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const TransfersPage = lazy(() => import("./pages/TransfersPage"));
const RedesignPage = lazy(() => import("./pages/RedesignPage"));
const SecurityAuditsPage = lazy(() => import("./pages/SecurityAuditsPage"));
const LandingPagesPage = lazy(() => import("./pages/LandingPagesPage"));
const WebsiteOptimisationPage = lazy(() => import("./pages/WebsiteOptimisationPage"));
const OneOffSupportPage = lazy(() => import("./pages/OneOffSupportPage"));
const GoogleBusinessProfilePage = lazy(() => import("./pages/GoogleBusinessProfilePage"));
const WebsiteReportingPage = lazy(() => import("./pages/WebsiteReportingPage"));
const ContentWritingPage = lazy(() => import("./pages/ContentWritingPage"));
const WebsiteAssistantsPage = lazy(() => import("./pages/WebsiteAssistantsPage"));
const WebsiteDesignPage = lazy(() => import("./pages/WebsiteDesignPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <IframeScrollHandler />
        <ScrollToHash />
        <Header />
        <main>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about-us" element={<AboutPage />} />
              <Route path="/portfolio" element={<PortfolioPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/managed-hosting" element={<HostingPage />} />
              <Route path="/website-care-plans" element={<SupportPage />} />
              <Route path="/business-email-setup" element={<EmailPage />} />
              <Route path="/website-transfers" element={<TransfersPage />} />
              <Route path="/website-redesign" element={<RedesignPage />} />
              <Route path="/security-audits" element={<SecurityAuditsPage />} />
              <Route path="/landing-pages" element={<LandingPagesPage />} />
              <Route path="/website-optimisation" element={<WebsiteOptimisationPage />} />
              <Route path="/one-off-website-support" element={<OneOffSupportPage />} />
              <Route path="/google-business-profile" element={<GoogleBusinessProfilePage />} />
              <Route path="/website-reporting" element={<WebsiteReportingPage />} />
              <Route path="/content-writing" element={<ContentWritingPage />} />
              <Route path="/website-assistants" element={<WebsiteAssistantsPage />} />
              <Route path="/website-design" element={<WebsiteDesignPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/blog/admin" element={<BlogAdminPage />} />
              <Route path="/seo/admin" element={<SEOAdminPage />} />
              <Route path="/faq/admin" element={<FAQAdminPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-and-conditions" element={<TermsPage />} />
              <Route path="/birmingham" element={<LocationPage />} />
              <Route path="/redditch" element={<LocationPage />} />
              <Route path="/dudley" element={<LocationPage />} />
              <Route path="/bromsgrove" element={<LocationPage />} />
              <Route path="/solihull" element={<LocationPage />} />
              <Route path="/worcester" element={<LocationPage />} />
              <Route path="/henley-in-arden" element={<LocationPage />} />
              <Route path="/404" element={<NotFoundPage />} />
              {/* 301 Redirects: old location URLs → new URLs */}
              <Route path="/web-design-birmingham" element={<Navigate to="/birmingham" replace />} />
              <Route path="/web-design-redditch" element={<Navigate to="/redditch" replace />} />
              <Route path="/web-design-dudley" element={<Navigate to="/dudley" replace />} />
              <Route path="/web-design-bromsgrove" element={<Navigate to="/bromsgrove" replace />} />
              <Route path="/web-design-solihull" element={<Navigate to="/solihull" replace />} />
              <Route path="/web-design-worcester" element={<Navigate to="/worcester" replace />} />
              <Route path="/web-design-henley-in-arden" element={<Navigate to="/henley-in-arden" replace />} />
              {/* 301 Redirects: old service URLs → new URLs */}
              <Route path="/redesign" element={<Navigate to="/website-redesign" replace />} />
              <Route path="/hosting" element={<Navigate to="/managed-hosting" replace />} />
              <Route path="/transfers" element={<Navigate to="/website-transfers" replace />} />
              <Route path="/support" element={<Navigate to="/website-care-plans" replace />} />
              <Route path="/email" element={<Navigate to="/business-email-setup" replace />} />
              <Route path="/about" element={<Navigate to="/about-us" replace />} />
              <Route path="/care-plans" element={<Navigate to="/website-care-plans" replace />} />
              {/* 301 Redirects: old legal URLs → new URLs */}
              <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
              <Route path="/terms" element={<Navigate to="/terms-and-conditions" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </main>
        <ScrollToTopButton />
        <CookieConsent />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
